/**
 * Pure model of the cabinet's add/edit form and its page-level derivations
 * (T.Y8, SR §5.2). Framework-free on purpose — the `unit` Playwright project
 * runs in plain Node, so this file may not value-import `./client` or a
 * component. Everything the drawer does BEFORE a byte moves lives here.
 *
 * The ФИО rule mirrors the server's `isFullFioFormat` (`@cb-manager/shared`
 * `address/fio.ts`), which the SPA cannot import into a plain-Node unit test:
 * NFKC fold, `ё → е`, hyphen-like dashes joined into the word, stray accents and
 * apostrophes folded away, then TWO OR MORE tokens of letters (any one alphabet per
 * token) with `-` allowed inside a token. A rule that exists in two places is stated
 * in both and tested in both.
 */

import type { CuratorChild, CuratorPrefillRow, CuratorVenue } from '../../api/curator-model';
import { PREFILL_GROUP_NONE } from '../../api/curator-model';

// ADDITIVE (v1.8.8, T.Z43) — the audience fold, shared with the api and the register's
// filter. Restated rule (see the module doc): a fold that exists in two places is
// stated in both and tested in both.
// ADDITIVE (v1.9.64, T.Z195) — the same shared fold the register's «Провалил ЕВИ»
// chip speaks: ONE fold of the auto and the manual fact, never a cabinet-local copy.
import { audienceBucketOf, eviFailedChip, type AudienceFilter } from '@cb-manager/shared';

import { hasAtLeast } from '../../auth/roles';
// ADDITIVE (v1.9.64, T.Z195) — the chase cells are the REGISTER's own views
// (`contractCellText`, `contractDeadlineView`, `enrolledView`, the tone maps and the
// two chip titles), imported rather than restated: this file is framework-free and so
// is that one, so the plain-Node unit project keeps running both.
import {
  AUDIENCE_LABEL,
  CONTRACT_STATE_TONE,
  CONTRACT_DEADLINE_CHIP_TITLE,
  EVI_FAILED_CHIP_TITLE,
  contractCellText,
  contractDeadlineView,
  enrolledView,
} from '../applications/applications-model';
import { pluralRu } from '../venues/venues-model';
import type { ComboboxOption } from '../../components/combobox-model';

// ---------------------------------------------------------------------------
// ФИО
// ---------------------------------------------------------------------------

/** One name word: letters (any one alphabet), hyphenated parts included. */
const FIO_WORD = /^\p{L}+(?:-\p{L}+)*$/u;

/** The two alphabets the data actually carries — a token carrying BOTH is a copy slip. */
const FIO_HAS_CYRILLIC = /[а-я]/;
const FIO_HAS_LATIN = /[a-z]/;

/** Unicode hyphens/dashes that join into the word — the shared `HYPHEN_LIKE` family,
 * spelled out in escapes because a literal dash in source is invisible to review. */
const FIO_HYPHEN_LIKE = new RegExp(
  '[\\u00ad\\u2010\\u2011\\u2012\\u2013\\u2014\\u2015\\u2212\\ufe58\\ufe63\\uff0d]',
  'g',
);

/** Punctuation INSIDE a name token («О'Коннор») — folded away before tokenising. */
const FIO_INTRA_WORD_NOISE = /['’`´]/g;

/** Combining marks a word-processor copy/paste leaves behind. */
const FIO_MARKS = /\p{M}/gu;

/** The folded tokens — the server's `fioTokens`, restated (see the module doc). */
function fioTokens(raw: string): string[] {
  if (typeof raw !== 'string') return [];
  let folded = raw.normalize('NFKC');
  folded = folded.replace(FIO_HYPHEN_LIKE, '-');
  folded = folded.toLocaleLowerCase('ru-RU');
  folded = folded.replace(/ё/g, 'е');
  folded = folded.normalize('NFD').replace(FIO_MARKS, '');
  folded = folded.replace(FIO_INTRA_WORD_NOISE, '');
  return folded.split(/\s+/).filter((token) => token.length > 0);
}

/** The folded verdict — the server's own rule, restated (see the module doc). */
export function isFullFioFormat(raw: string): boolean {
  const tokens = fioTokens(raw);
  if (tokens.length < 2) return false;
  return tokens.every(
    (token) => FIO_WORD.test(token) && !(FIO_HAS_CYRILLIC.test(token) && FIO_HAS_LATIN.test(token)),
  );
}

export const FIO_PLACEHOLDER = 'Фамилия Имя Отчество';
export const FIO_HINT =
  'Минимум два слова буквами, как в ЕПГУ: Фамилия Имя Отчество. Дефис и апостроф внутри слова допустимы.';
const FIO_ERROR =
  'ФИО должно состоять минимум из двух слов буквами; цифры и знаки препинания внутри слова недопустимы.';

/**
 * The ФИО error, or `null` when the value would pass the api. Empty is NOT an
 * error here — an empty required field is the submit gate's business, and lecturing
 * about an untouched field reads as noise.
 */
export function fioError(raw: string): string | null {
  const value = raw.trim();
  if (value === '') return null;
  return isFullFioFormat(value) ? null : FIO_ERROR;
}

// ---------------------------------------------------------------------------
// Identifiers
// ---------------------------------------------------------------------------

/** «Номер заявления»: 10 digits, `7` or `8` first — the api's `PGU_ID_PATTERN`. */
export const PGU_ID_PATTERN = /^[78]\d{9}$/;
/** The УНТИ leaf id: 1–12 digits. */
export const UNTI_ID_PATTERN = /^\d{1,12}$/;

export const PGU_ID_HINT = 'Номер заявления из ЕПГУ, если известен — по нему ребёнок находится в первую очередь.';
export const PGU_ID_ERROR = 'Номер заявления — 10 цифр и начинается с 7 или 8.';
export const UNTI_ID_ERROR = 'УНТИ ID — от 1 до 12 цифр.';

export function pguIdError(raw: string): string | null {
  const value = raw.trim();
  if (value === '') return null;
  return PGU_ID_PATTERN.test(value) ? null : PGU_ID_ERROR;
}

export function untiIdError(raw: string): string | null {
  const value = raw.trim();
  if (value === '') return null;
  return UNTI_ID_PATTERN.test(value) ? null : UNTI_ID_ERROR;
}

// ---------------------------------------------------------------------------
// The draft
// ---------------------------------------------------------------------------

export interface PrefillDraft {
  fullName: string;
  pguId: string;
  untiId: string;
  region: string;
  /**
   * The programme text AS THE ROW STORES IT — the legacy free text a partner (or a
   * pre-T.Z58 drawer) wrote. Never editable in the drawer since T.Z58: the programme
   * is chosen from the dictionary now, and a chosen programme rewrites this to the
   * dictionary's own value on save.
   */
  programName: string;
  /**
   * ADDITIVE (v1.9.3, T.Z58) — «Программа», the chosen dictionary programme: a
   * {@link PrefillProgramGroup} key (the folded short name), `''` = none chosen.
   * REQUIRED for a new row (the owner's rule, carried over from the «Курс» era).
   * The audience is NOT a draft field — it DERIVES from this choice, which is the
   * owner's «заполняется автоматически из программы».
   */
  programKey: string;
  /**
   * ADDITIVE (v1.9.3, T.Z58) — «Формат», the half of the офл/онл pair the row
   * belongs to: `''` | `OFFLINE` | `ONLINE`. REQUIRED whenever a programme is chosen —
   * the pair resolves to ONE course only through it.
   */
  format: string;
}

export function emptyPrefillDraft(): PrefillDraft {
  return { fullName: '', pguId: '', untiId: '', region: '', programName: '', programKey: '', format: '' };
}

/**
 * May the drawer's fields take input at all? (Z4, `canEdit` of #26.2.)
 *
 * `isEdit && canEdit` was the wrong gate: on CREATE there is no row, so the
 * conjunction was false and every input — and with it the «Добавить» button —
 * rendered disabled, and «Добавить ребёнка» could not add a child. The rule is
 * an OR by shape: a CREATE has no server editability to respect, an EDIT does.
 */
export function prefillEditable(isEdit: boolean, canEdit: boolean): boolean {
  return !isEdit || canEdit;
}

/** The drawer's draft for an edit: the row's own fields, plus the programme RESOLVED. */
export function draftOfRow(row: CuratorPrefillRow, programs: ReadonlyArray<PrefillCourseSource>): PrefillDraft {
  const draft: PrefillDraft = {
    fullName: row.fullName,
    pguId: row.pguId ?? '',
    untiId: row.untiId ?? '',
    region: row.region ?? '',
    programName: row.programName ?? '',
    programKey: '',
    format: '',
  };

  // A MATCHED (or otherwise resolved) child speaks OUR database: the course decides the
  // programme and the format, and the curator's text is history. T.Z58 — «смэтченные
  // дети — поля из нашей БД».
  const course =
    row.courseId === null || row.courseId === undefined
      ? undefined
      : programs.find((candidate) => candidate.courseId === row.courseId);
  if (course !== undefined) {
    draft.programKey = programGroupKeyOf(course);
    draft.format = course.formats[0] ?? '';
    return draft;
  }

  // The already-typed free text: matched against the dictionary FOLD-EXACT and shown as
  // the selected value (the owner's migration ask). The verbatim stays in `programName`,
  // so an unmatched text is kept verbatim and an unmatched row still submits unchanged.
  const match = matchProgramText(row.programName ?? '', programs);
  if (match !== null) {
    draft.programKey = match.group.key;
    if (match.format !== null) draft.format = match.format;
  }
  return draft;
}

/** The new-child draft seeded from the working table's active filters (T.Z58). */
export function prefillDraftFromFilters(
  filters: { courseId?: number; audience?: string } | undefined,
  programs: ReadonlyArray<PrefillCourseSource>,
): PrefillDraft {
  const draft = emptyPrefillDraft();
  const course =
    filters?.courseId === undefined ? undefined : programs.find((candidate) => candidate.courseId === filters.courseId);
  if (course !== undefined) {
    // The «Программа» filter names ONE course — its short name and its format are
    // exactly the two drawer fields the table was narrowed by.
    draft.programKey = programGroupKeyOf(course);
    draft.format = course.formats[0] ?? '';
  }
  return draft;
}

/**
 * The dictionary-arrival merge (the T.Z58 e2e-auth fix). The seed — the filter prefill
 * for a new row, the row's own resolution for an edit — can only be computed once the
 * #21 dictionary has arrived, and a cold mount can open the drawer over a query still
 * in flight. Seeding used to REPLACE the draft at that moment, so everything the
 * curator had typed before the read landed (the ФИО first of all) was thrown away:
 * the form then showed «Введите ФИО ребёнка.» with the submit gate closed for good —
 * caught by the e2e-auth `manager-prefill` case, and reachable by any curator typing
 * on a slow dictionary read. The rule now: only the programme/format half moves, only
 * while the curator has chosen no programme themselves, and a seed without a
 * programme never touches a format the curator already picked.
 */
export function seedPrefillDraftProgram(
  current: PrefillDraft,
  seeded: Pick<PrefillDraft, 'programKey' | 'format'>,
): PrefillDraft {
  if (current.programKey.trim() !== '') return current;
  if (seeded.programKey.trim() !== '') return { ...current, programKey: seeded.programKey, format: seeded.format };
  return current.format.trim() === '' ? { ...current, format: seeded.format } : current;
}

export interface PrefillDraftCheck {
  errors: Partial<Record<keyof PrefillDraft, string>>;
  /** `false` while a required field is missing or a shape rule is broken. */
  canSubmit: boolean;
  /** The trimmed values, or the body the api would refuse — never sent. */
  patch: PrefillPatchBody;
}

/** The optional fields of #26.3, absent when the curator left them empty. */
export type PrefillPatchBody = {
  fullName: string;
  pguId?: string;
  region?: string;
  untiId?: string;
  programName?: string;
  /**
   * ADDITIVE (v1.9.3, T.Z58) — the programme triple the api resolves into the course
   * (#26.3 `audience`/`format`, v1.9.3). `courseId` is deliberately absent here except
   * for the one patch case that needs it (the explicit clear) — the RESOLUTION is the
   * server's, the drawer never computes a course id.
   */
  audience?: string;
  format?: 'OFFLINE' | 'ONLINE';
  courseId?: number;
};

/** The error a new row without a programme gets (the owner: the programme is required). */
export const PROGRAM_REQUIRED_ERROR = 'Выберите программу из словаря.';
/** The error a chosen programme without a format gets — the pair needs both halves. */
export const FORMAT_REQUIRED_ERROR = 'Выберите формат: офлайн или онлайн.';

/**
 * Validates the draft and folds it into the body. «Рекомендуется» is a UI weight,
 * not a rule: an empty `pguId` still submits (the row then matches by ФИО, and the
 * matcher answers `NO_ID_AND_NO_NAME_HIT` if it cannot) — the hint says so.
 */
export function checkDraft(
  draft: PrefillDraft,
  options: { programs?: ReadonlyArray<PrefillCourseSource>; requireCourse?: boolean } = {},
): PrefillDraftCheck {
  const fullName = draft.fullName.trim();
  const pguId = draft.pguId.trim();
  const untiId = draft.untiId.trim();
  const region = draft.region.trim();
  const programName = draft.programName.trim();
  const programKey = draft.programKey.trim();
  const format = draft.format.trim();

  const errors: Partial<Record<keyof PrefillDraft, string>> = {};
  if (fullName === '') errors.fullName = 'Введите ФИО ребёнка.';
  else {
    const fio = fioError(fullName);
    if (fio !== null) errors.fullName = fio;
  }

  const pgu = pguIdError(pguId);
  if (pgu !== null) errors.pguId = pgu;
  const unti = untiIdError(untiId);
  if (unti !== null) errors.untiId = unti;

  // ADDITIVE (v1.9.3, T.Z58) — the programme/format pair: each is required for a NEW
  // row, and a half-chosen pair (a programme without a format, a format without a
  // programme) is an error in both modes — only the complete triple names a course.
  const group = programKey === '' ? null : findProgramGroup(programKey, options.programs ?? []);
  if (programKey === '') {
    if (format !== '') errors.programKey = PROGRAM_REQUIRED_ERROR;
    else if (options.requireCourse) errors.programKey = PROGRAM_REQUIRED_ERROR;
  }
  if (programKey !== '' && format === '') errors.format = FORMAT_REQUIRED_ERROR;
  if (programKey !== '' && group === null) errors.programKey = PROGRAM_REQUIRED_ERROR;

  const patch: PrefillPatchBody = { fullName };
  if (pguId !== '') patch.pguId = pguId;
  if (region !== '') patch.region = region;
  if (untiId !== '') patch.untiId = untiId;
  // A CHOSEN programme rewrites the stored text to the dictionary's own value (the
  // owner: «смэтченные дети — поля из нашей БД»); an unmatched legacy text stays
  // verbatim, which is what keeps a course-less row's wording alive on an edit.
  if (group !== null && format !== '') {
    patch.programName = group.label;
    if (group.audience !== null) patch.audience = group.audience;
    patch.format = format as 'OFFLINE' | 'ONLINE';
  } else if (group === null && programName !== '') {
    patch.programName = programName;
  }

  return { errors, canSubmit: Object.keys(errors).length === 0, patch };
}

/** The #26.5 body: only the keys the curator actually changed, `null` clears. */
export type CuratorPrefillPatchInput = {
  fullName?: string;
  region?: string | null;
  pguId?: string | null;
  untiId?: string | null;
  programName?: string | null;
  /** ADDITIVE (v1.9.3, T.Z58) — the triple hints; `courseId: null` clears the course. */
  audience?: string;
  format?: 'OFFLINE' | 'ONLINE';
  courseId?: number | null;
};

/**
 * The PATCH diff against the row as the server has it. A field the curator
 * emptied is sent as `null` (the contract's clearing value); a field they never
 * touched is absent — a no-op body is never sent, so the api does not spend an
 * audit row on a click.
 */
export function patchOfDraft(
  draft: PrefillDraft,
  row: CuratorPrefillRow,
  programs: ReadonlyArray<PrefillCourseSource>,
): CuratorPrefillPatchInput {
  const check = checkDraft(draft, { programs });
  if (!check.canSubmit) return {};

  const patch: CuratorPrefillPatchInput = {};
  const fullName = check.patch.fullName;
  if (fullName !== row.fullName) patch.fullName = fullName;

  const pguId = check.patch.pguId ?? null;
  if (pguId !== (row.pguId ?? null)) patch.pguId = pguId;

  const untiId = check.patch.untiId ?? null;
  if (untiId !== (row.untiId ?? null)) patch.untiId = untiId;

  const region = check.patch.region ?? null;
  if (region !== (row.region ?? null)) patch.region = region;

  // ADDITIVE (v1.9.3, T.Z58) — the programme block. The row's own triple stands in the
  // dictionary (`courseId` → its course); the draft's stands in the form. The pair
  // (programme × format) is what the api resolves, so a change to either half — or a
  // course appearing where none was — sends the whole triple, never a bare text edit.
  const rowCourse =
    row.courseId === null || row.courseId === undefined
      ? undefined
      : programs.find((candidate) => candidate.courseId === row.courseId);
  const rowKey = rowCourse === undefined ? null : programGroupKeyOf(rowCourse);
  const rowFormat = rowCourse === undefined ? null : rowCourse.formats[0] ?? null;
  const group = draft.programKey.trim() === '' ? null : findProgramGroup(draft.programKey.trim(), programs);
  const chosenFormat = draft.format.trim() === '' ? null : (draft.format.trim() as 'OFFLINE' | 'ONLINE');

  if (group !== null && chosenFormat !== null) {
    const sameCourse = rowCourse !== undefined && rowKey === group.key && rowFormat === chosenFormat;
    if (!sameCourse) {
      patch.programName = group.label;
      if (group.audience !== null) patch.audience = group.audience;
      patch.format = chosenFormat;
    }
  } else {
    // Nothing chosen: the legacy text diff only, and — when the row HAD a course — the
    // explicit clear (the contract's clearing value; the curator deleted the programme).
    const programName = check.patch.programName ?? null;
    if (programName !== (row.programName ?? null)) patch.programName = programName;
    if (rowCourse !== undefined) patch.courseId = null;
  }

  return patch;
}

// ---------------------------------------------------------------------------
// ADDITIVE (v1.9.3, T.Z58) — «Программа» × «Аудитория» × «Формат»: the dictionary
// the drawer picks from (the #21 read, grouped into the owner's programmes)
// ---------------------------------------------------------------------------

/** The slice of the #21 programme dictionary the drawer reads. */
export interface PrefillCourseSource {
  courseId: number;
  name: string;
  audience: string | null;
  formats: ReadonlyArray<'OFFLINE' | 'ONLINE'>;
  shortName?: string | null;
}

/**
 * T.Z64 — what the drawer and the filter say about the #21 dictionary read itself.
 *
 * The bug this state exists for: a FAILED read used to be folded into
 * `data?.data ?? []`, so a 403 (or any refusal) looked exactly like an empty
 * dictionary — the combobox said «Программа не найдена» over zero options and typing
 * did nothing, and the curator had no way to tell «нет данных» from «сломалось». The
 * state is computed from the query's flags, never from the array length: an EMPTY but
 * successful read stays `ready` (the honest «no programmes yet»), only a FAILED read
 * becomes `error`.
 *
 * The argument is the structural slice of the react-query result the model needs —
 * plain flags, so this file stays framework-free for the plain-Node unit project.
 */
export type PrefillDictionaryState =
  /** The read is still on the wire — the drawer keeps its first-render seeding idle. */
  | { kind: 'loading' }
  /** The read FAILED (403, 500, offline): the surfaces must show the error, loudly. */
  | { kind: 'error' }
  /** The read answered — `data` may still be empty, which is a fact, not a failure. */
  | { kind: 'ready' };

/** The one message both surfaces render for the `error` state (a retry is offered next to it). */
export const PREFILL_DICTIONARY_ERROR =
  'Словарь программ не загрузился. Выбор программы недоступен — повторите попытку.';

export function prefillDictionaryState(query: {
  isPending?: boolean;
  isError?: boolean;
  isSuccess?: boolean;
}): PrefillDictionaryState {
  if (query.isError === true) return { kind: 'error' };
  if (query.isSuccess === true) return { kind: 'ready' };
  return { kind: 'loading' };
}

export const FORMAT_SHORT_LABEL: Readonly<Record<string, string>> = {
  OFFLINE: 'офл',
  ONLINE: 'онл',
};

/** The two options of the «Формат» select, in the owner's own wording. */
export const PREFILL_FORMAT_OPTIONS: ReadonlyArray<{ value: 'OFFLINE' | 'ONLINE'; label: string }> = [
  { value: 'OFFLINE', label: 'офлайн' },
  { value: 'ONLINE', label: 'онлайн' },
];

/** The audience half of an option's hint — the same three buckets the filters speak. */
export function prefillAudienceLabel(audience: string | null): string | null {
  const bucket = audienceBucketOf(audience);
  if (bucket === null) return audience === null || audience.trim() === '' ? null : audience;
  return AUDIENCE_LABEL[bucket] ?? bucket;
}

/**
 * The working table's «Программа» FILTER list (T.Z43, unchanged by T.Z58): ONE option
 * per COURSE of the dictionary (#21), captioned by the short name with the format pair
 * as the hint — twelve rows over six captions. The filter keeps speaking courses
 * (`?courseId=`), because that is what the api stores; it is the DRAWER that stopped
 * asking for one.
 */
export function prefillCourseOptions(programs: ReadonlyArray<PrefillCourseSource>): ComboboxOption[] {
  return programs.map((course) => {
    const caption = course.shortName?.trim() || course.name;
    const formats = course.formats.map((format) => FORMAT_SHORT_LABEL[format] ?? format).join('/');
    const audience = prefillAudienceLabel(course.audience);
    const hint = [audience, formats === '' ? null : formats].filter((part) => part !== null).join(' · ');
    return { value: String(course.courseId), label: caption, hint: hint === '' ? course.name : hint };
  });
}

/**
 * The comparison form of a programme text — the api's `normalizeSpreadsheetText`
 * (`apps/api/src/curator/prefill-import.ts`) restated for the browser. NFKC, the dash
 * family unified, lowercase `ru`, `ё → е`, whitespace collapsed: «ИИ 8–9» typed, «ИИ
 * 8-9» seeded and `programs.short_name` are ONE programme either way. The SPA cannot
 * import the api module (plain-Node unit project), so a rule that exists in two places
 * is stated in both and tested in both — the file's own convention since T.Y8.
 */
export function foldProgramText(value: string): string {
  if (typeof value !== 'string') return '';
  return value
    .normalize('NFKC')
    .replace(/[‐-―−­]/g, '-')
    .toLocaleLowerCase('ru')
    .replace(/ё/g, 'е')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * One PROGRAMME of the owner's dictionary: the офл/онл PAIR a short name names. The
 * campaign's 12 courses are 6 programmes × 2 formats, and the drawer speaks
 * programmes (the course was the internal concept T.Z58 removed). The key is the
 * FOLDED label, so the free-text match and the combobox option share one identity.
 */
export interface PrefillProgramGroup {
  key: string;
  /** The caption the combobox shows: the short name, else the course name. */
  label: string;
  /** The audience bucket the programme addresses (the pair shares it) — the auto-fill. */
  audience: AudienceFilter | null;
  audienceLabel: string | null;
  /** The pair, by format: the course each half of the selectbox resolves to. */
  byFormat: { OFFLINE?: PrefillCourseSource; ONLINE?: PrefillCourseSource };
}

/** The group identity of one course — the folded short name, else the folded name. */
export function programGroupKeyOf(course: PrefillCourseSource): string {
  return foldProgramText(course.shortName?.trim() || course.name);
}

/** The dictionary (12 courses) folded into the programmes (6 pairs) the drawer offers. */
export function prefillProgramGroups(programs: ReadonlyArray<PrefillCourseSource>): PrefillProgramGroup[] {
  const groups = new Map<string, PrefillProgramGroup>();
  for (const course of programs) {
    const key = programGroupKeyOf(course);
    let group = groups.get(key);
    if (group === undefined) {
      group = {
        key,
        label: course.shortName?.trim() || course.name,
        audience: audienceBucketOf(course.audience),
        audienceLabel: prefillAudienceLabel(course.audience),
        byFormat: {},
      };
      groups.set(key, group);
    }
    for (const format of course.formats) group.byFormat[format] = course;
  }
  return [...groups.values()];
}

function findProgramGroup(
  key: string,
  programs: ReadonlyArray<PrefillCourseSource>,
): PrefillProgramGroup | null {
  return prefillProgramGroups(programs).find((group) => group.key === key) ?? null;
}

/** What the fold-exact match found: the programme, plus the course the text itself named. */
export interface PrefillProgramMatch {
  group: PrefillProgramGroup;
  /** Set when the text spelled out ONE course of the pair (its «(онлайн)» name, say). */
  format: 'OFFLINE' | 'ONLINE' | null;
}

/**
 * The already-typed free text, matched against the dictionary FOLD-EXACT (the owner's
 * T.Z58 ask). The api's ladder order: fold-exact `short_name` first — that names the
 * PAIR, so the format stays open for the selectbox — then fold-exact `name`, which
 * names ONE course and therefore prefills the format. No fuzzy hit, no suggestion: an
 * unmatched text stays verbatim on the row and the field reads as unmatched.
 */
export function matchProgramText(
  text: string,
  programs: ReadonlyArray<PrefillCourseSource>,
): PrefillProgramMatch | null {
  const folded = foldProgramText(text);
  if (folded === '') return null;
  const groups = prefillProgramGroups(programs);

  const byLabel = groups.find((group) => group.key === folded);
  if (byLabel !== undefined) return { group: byLabel, format: null };

  for (const course of programs) {
    if (foldProgramText(course.name) !== folded) continue;
    const group = groups.find((candidate) => programGroupKeyOf(course) === candidate.key);
    if (group !== undefined) return { group, format: course.formats[0] ?? null };
  }
  return null;
}

/** The combobox's options: one per programme, the audience as the hint. */
export function prefillProgramOptions(
  programs: ReadonlyArray<PrefillCourseSource>,
  audience?: string,
): ComboboxOption[] {
  return prefillProgramGroups(programs)
    .filter((group) => audience === undefined || audience === '' || group.audience === audience)
    .map((group) => ({
      value: group.key,
      label: group.label,
      hint: group.audienceLabel ?? undefined,
    }));
}

/**
 * The one-line summary under the programme fields — the course the triple (программа ×
 * аудитория × формат) resolves to in the dictionary, the owner's own vocabulary. What
 * the drawer REMOVED (the «Курс» field) now reads as a confirmation line: the curator
 * sees which course the row will carry before anything is written. `null` while the
 * triple is incomplete.
 */
export function prefillCourseSummary(
  draft: Pick<PrefillDraft, 'programKey' | 'format'>,
  programs: ReadonlyArray<PrefillCourseSource>,
): string | null {
  const group = draft.programKey.trim() === '' ? null : findProgramGroup(draft.programKey.trim(), programs);
  if (group === null) return null;
  const rawFormat = draft.format.trim();
  const format = rawFormat === '' ? null : (rawFormat as 'OFFLINE' | 'ONLINE');
  const course = format === null ? undefined : group.byFormat[format];
  if (format === null || course === undefined) {
    return group.audienceLabel === null
      ? `${group.label} · формат не выбран`
      : `${group.label} · ${group.audienceLabel} · формат не выбран`;
  }
  return [group.label, group.audienceLabel, FORMAT_SHORT_LABEL[format] ?? format]
    .filter((part) => part !== null && part !== '')
    .join(' · ');
}

// ---------------------------------------------------------------------------
// Page-level derivations
// ---------------------------------------------------------------------------

/**
 * The venue picker's «Все места» value, and the selection it maps to (T.Y18 owner
 * item): the whole scope in one screen. `#26.2` answers the WHOLE scope when
 * `venueId` is absent, so the online rows aggregate for free; the summaries sum
 * venue by venue ({@link aggregateOffline}/{@link aggregateOnline}). The offline
 * ROSTER stays venue-scoped by contract (#26.1 requires a `venueId`), so the
 * aggregate view shows the offline half as numbers and points at the picker — and
 * when the scope has no offline half at all (T.Z48, {@link hasOfflineHalf}) neither
 * the numbers nor the pointer render.
 */
export const ALL_VENUES_VALUE = 'all';

/** What the tables are scoped to: one venue of the scope, or all of them. */
export type VenueSelection = { kind: 'all' } | { kind: 'venue'; venueId: number };

/**
 * The selection out of the URL. A value outside the scope (a stale link, a revoked
 * venue) falls back to the first venue — the api would 403 that request, and the
 * fallback spares the curator the error screen. An empty scope has no selection.
 */
export function parseVenueSelection(venues: readonly CuratorVenue[], raw: string | null): VenueSelection | null {
  if (venues.length === 0) return null;
  if (raw === ALL_VENUES_VALUE) return { kind: 'all' };
  const wanted = readVenueId(raw);
  if (wanted !== null && venues.some((venue) => venue.venueId === wanted)) return { kind: 'venue', venueId: wanted };
  const first = venues[0]?.venueId;
  return first === undefined ? null : { kind: 'venue', venueId: first };
}

/** The URL value of a selection — `'all'` or the numeric venue id. */
export function venueSelectionParam(selection: VenueSelection): string {
  return selection.kind === 'all' ? ALL_VENUES_VALUE : String(selection.venueId);
}

function readVenueId(raw: string | null): number | null {
  const parsed = Number.parseInt(raw ?? '', 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

/**
 * The venue the tables are scoped to (the pre-«Все места» rule, kept for the
 * venue-scoped controls). The URL carries the choice; a value outside the scope
 * (a stale link, a revoked venue) falls back to the first of the scope — the api
 * would 403 the request, and the fallback spares the curator that screen.
 */
export function selectedVenueId(venues: readonly CuratorVenue[], wanted: number | null): number | null {
  if (venues.length === 0) return null;
  if (wanted !== null && venues.some((venue) => venue.venueId === wanted)) return wanted;
  return venues[0]?.venueId ?? null;
}

/**
 * The label of the venue picker's option — THE NAME, nothing else (T.Z14/C10): the
 * «· внесение открыто» and «· онлайн» marks are gone. They made fifteen filials read
 * like a config dump, and the same facts are already on the screen the picker serves
 * (the online section exists exactly where the flag is on). The region stays the
 * option's hint — it is not part of the name and never was.
 */
export function venueOptionLabel(venue: CuratorVenue): { label: string; hint: string | undefined } {
  return { label: venue.name, hint: venue.region ?? undefined };
}

/** «Данные от …» — the freshness line under a section header (SR §5.2). */
export function freshnessLine(venue: CuratorVenue, format: (iso: string | null) => string): string {
  const parts: string[] = [];
  if (venue.lastEviSnapshotAt !== null) parts.push(`ЕВИ от ${format(venue.lastEviSnapshotAt)}`);
  if (venue.lastUntiSyncAt !== null) parts.push(`УНТИ от ${format(venue.lastUntiSyncAt)}`);
  return parts.length === 0 ? 'Данные ещё не поступали' : `Данные: ${parts.join(' · ')}`;
}

// ---------------------------------------------------------------------------
// Scope-level views («Все филиалы»)
// ---------------------------------------------------------------------------

/**
 * The offline numbers of a whole scope, summed venue by venue — or `null` when NO
 * venue of the scope carries an offline section (T.Z48): `null` is what the summary
 * strip already renders as «no offline here» for a single venue, so the aggregated
 * view speaks the same language instead of printing «План офлайн 0 · Назначено 0 из 0»
 * to a curator whose scope has no offline half at all. A scope WITH an offline venue
 * keeps its numbers even when they sum to zero — the section exists, «0 из 0» is
 * honest context there.
 */
export function aggregateOffline(venues: readonly CuratorVenue[]): { planOffline: number; offlineAssigned: number } | null {
  let planOffline = 0;
  let offlineAssigned = 0;
  let offlineVenues = 0;
  for (const venue of venues) {
    if (venue.summary.offline === null) continue;
    offlineVenues += 1;
    planOffline += venue.summary.offline.planOffline;
    offlineAssigned += venue.summary.offline.offlineAssigned;
  }
  return offlineVenues === 0 ? null : { planOffline, offlineAssigned };
}

/**
 * Does the scope have an offline half AT ALL (T.Z48) — the page's one offline gate.
 * `#26` already publishes the answer per venue (`summary.offline` is non-null exactly
 * for a venue with an offline section, D6), so the fold reads the envelope and adds
 * no contract field: the header line, the summary chips, the roster block and the
 * «где искать» hint all key off it, and an online-only curator's page renders none.
 */
export function hasOfflineHalf(venues: readonly CuratorVenue[]): boolean {
  return venues.some((venue) => venue.summary.offline !== null);
}

/**
 * T.Z110 (owner, 2026-09-17) — does THIS venue have an ONLINE half at all: the gate
 * that says «the section exists here», as opposed to «the set is led on another
 * venue». Before T.Z110 the gate was the api's `summary.online !== null` alone, i.e.
 * the prefill flag — an OFFLINE venue (kind `REGISTRY`) carries the flag off, so its
 * curator saw the roster and a «ведётся на других площадках» hint while the venue in
 * fact holds online-format children too. The owner's end state: «curator will have
 * 2 tables offline & online per offline venue».
 *
 * The rule, per venue:
 *
 *   * kind `REGISTRY` → ALWAYS (both tables). The online rows come from #26.2, which
 *     has never filtered by kind or flag on reads — scope only — so the table is
 *     already answerable for an offline venue and the gate was web-only.
 *   * kind `PARTNER_ONLINE` → the flag decides, as T.Z48 left it (no flag, no set,
 *     and the page says where the set is led instead of an empty table).
 *
 * The WRITE half stays where it was: `canAdd` keeps reading `allowCuratorChildren`
 * and the api's #26.3–#26.5 keep refusing a flagged-off venue with
 * `PREFILL_NOT_ALLOWED` — the flag is the owner's own switch for «внесение открыто»,
 * at an offline venue exactly as at an online one. T.Z110 widens what a curator SEES,
 * not what they may write.
 *
 * v1.9.26 (T.Z125) — the SET the table answers is the venue's online children: the
 * working rows PLUS the venue's ONLINE-set applications (the api widened #26.2), so
 * this gate no longer decides what the table CONTAINS — only whether the venue has an
 * online half to speak about (the pointer's job). The visibility rule itself moved to
 * {@link venueShowsOnlineTable}.
 */
export function venueHasOnlineHalf(venue: CuratorVenue): boolean {
  return venue.kind === 'REGISTRY' || venue.summary.online !== null;
}

/**
 * T.Z125 (owner refinement 2026-09-17) — does THIS venue's view render the ONLINE
 * children table? The owner's rule, over the venue's online children
 * ({@link venueChildCounts} — the working set plus the ONLINE-set rows since the api
 * widened #26.2):
 *
 *   * online children present → the table shows, quota or not (existing children stay
 *     manageable — a quota can never take rows off the screen);
 *   * no online children → the empty table (with «Добавить ребёнка» where the venue
 *     flag allows the write) shows ONLY when an online quota is assigned to the venue
 *     (`summary.online.planOnline > 0` — the venue's own `venue_plans.plan_online`,
 *     the mirror of the offline half's `planOffline`; NOT the partner pool, which is
 *     shared across branches and lives on #26's `partner` block);
 *   * no children and no quota → the table is hidden and the venue renders the
 *     offline-only view — there is nothing to fill, so no empty box either.
 *
 * A `PARTNER_ONLINE` venue keeps its T.Z110/T.Z48 behaviour: the flag decides (a
 * flagged branch shows its — possibly empty — set; an unflagged one has no half and
 * the pointer speaks). The switcher narrows past this gate either way
 * ({@link venueViewArrangement}).
 */
export function venueShowsOnlineTable(venue: CuratorVenue): boolean {
  if (venue.summary.online === null) return false;
  if (venueChildCounts(venue).online > 0) return true;
  return venue.kind === 'REGISTRY' ? venue.summary.online.planOnline > 0 : true;
}

// ---------------------------------------------------------------------------
// ADDITIVE (T.Z120, owner 2026-09-17) — the venue view's two count-widgets and the
// view switch they drive
// ---------------------------------------------------------------------------

/**
 * Which table the venue view's switch has narrowed to. `null` = nothing chosen —
 * BOTH tables render, which is T.Z110's arrangement and the switch's reset state.
 */
export type VenueViewSelection = 'offline' | 'online' | null;

/** The two children counts of one venue, read off #26 — no extra read anywhere. */
export interface VenueChildCounts {
  /** The offline roster's size (#26.1). */
  offline: number;
  /** The online working table's size (#26.2, the whole set — not the loaded page). */
  online: number;
}

/**
 * The counts the two widgets show ({@link VenueChildCounts}), taken from the venue
 * envelope #26 already answers — the page holds it before either table loads, so the
 * numbers never flicker with a table's own loading or pagination:
 *
 *   * offline — `summary.offline.offlineAssigned`. The api counts it under EXACTLY the
 *     predicate #26.1 lists the roster with (its own comment: «the summary number and
 *     the roster can never disagree»), so this IS the roster's row count;
 *   * online — the online table's WHOLE set, `prefillTotal + onlineAssigned` since the
 *     api widened #26.2 (v1.9.26, T.Z125): the venue's working rows PLUS its ONLINE-set
 *     applications. This is the number #26.2 answers as `meta.total` for the same
 *     scope, so the widget and the pager can never disagree — the pre-v1.9.26 reading
 *     (`prefillTotal` alone) is what left НПОК's 41 online children uncounted and the
 *     whole block hidden.
 *
 * The one honest gap: a venue with no online half publishes `summary.online: null` (D6
 * — since v1.9.26 a `REGISTRY` venue always publishes it; a flag-off partner venue
 * still does not), and this fold reads that as zero. Such a venue can therefore never
 * pass {@link venueHasBothKinds}, which is the owner's own rule: no online half, no
 * switch.
 */
export function venueChildCounts(venue: CuratorVenue): VenueChildCounts {
  return {
    offline: venue.summary.offline === null ? 0 : venue.summary.offline.offlineAssigned,
    online: venue.summary.online === null ? 0 : venue.summary.online.prefillTotal + venue.summary.online.onlineAssigned,
  };
}

/**
 * T.Z120 — the widgets' gate, the owner's words verbatim: the switch shows ONLY for a
 * venue that has offline AND online children ALTOGETHER. An online venue
 * (`PARTNER_ONLINE`) has no offline half, an offline venue without online rows has no
 * online one — either way there is nothing to switch between, so no widgets and the
 * venue renders its single table.
 */
export function venueHasBothKinds(venue: CuratorVenue): boolean {
  const counts = venueChildCounts(venue);
  return counts.offline > 0 && counts.online > 0;
}

/** One click on a widget: the clicked kind on, the SAME kind off again (back to both). */
export function nextVenueView(current: VenueViewSelection, clicked: 'offline' | 'online'): VenueViewSelection {
  return current === clicked ? null : clicked;
}

/** What the venue view renders for one switch state. */
export interface VenueViewArrangement {
  /** The two count-widgets render (only ever true for a both-kinds venue). */
  widgets: boolean;
  /** The offline roster block renders. */
  offlineTable: boolean;
  /** The online children block renders. */
  onlineTable: boolean;
  /**
   * The «офлайн-список ведётся на других площадках» pointer MAY show — the venue has
   * no offline half of its own. A table hidden by the switch or by the single-kind
   * rule is NOT that case: it lives here, the curator just asked for the other one.
   */
  offlineHint: boolean;
  /** The online twin: only for a venue that cannot hold an online set at all. */
  onlineHint: boolean;
}

/**
 * The venue view's table arrangement (T.Z120, refined by T.Z125):
 *
 *   * both kinds present → both tables while nothing is chosen (T.Z110's default), and
 *     the chosen kind ALONE once a widget is clicked;
 *   * the ONLINE half follows {@link venueShowsOnlineTable}: children present → shown;
 *     none → an honest empty table only where the venue carries an online quota to
 *     fill (`planOnline > 0`, a `REGISTRY` venue's own rule — a flagged
 *     `PARTNER_ONLINE` branch keeps showing its set), none and no quota → hidden, the
 *     venue renders the offline-only view. The T.Z120 «офлайн-площадка без
 *     онлайн-детей → только офлайн-таблица» case is this rule's «no children, no
 *     quota» corner now — the owner's refinement 2026-09-17 replaces the blanket
 *     hiding (a quota-ed venue must be able to START its online set from the cabinet);
 *   * a venue with NO children of either kind → both tables, unchanged: T.Z110's rule
 *     stands there («a venue with no children of a kind renders that table's honest
 *     empty state»), and the fresh venue's «Добавить ребёнка» lives on the online one —
 *     reachable wherever the quota rule shows that table.
 *
 * The pointers keep their T.Z48 meaning: they speak for a venue that has NO half at
 * all ({@link venueHasOnlineHalf}), never for one whose table is merely narrowed away
 * by the switch or hidden by the quota rule — «no quota» is not «led elsewhere».
 *
 * The offline half still keys off the venue's own section (`summary.offline !== null`,
 * D6) — T.Z120/T.Z125 narrow WHEN the tables show, never WHO may write (#26.3–#26.5
 * stay behind the venue flag).
 */
export function venueViewArrangement(venue: CuratorVenue, view: VenueViewSelection): VenueViewArrangement {
  const { offline, online } = venueChildCounts(venue);
  const onlineOnly = online > 0 && offline === 0;
  const hasOnlineHalf = venueHasOnlineHalf(venue);
  const showsOnline = venueShowsOnlineTable(venue);
  return {
    widgets: venueHasBothKinds(venue),
    offlineTable: venue.summary.offline !== null && view !== 'online' && !(view === null && onlineOnly),
    onlineTable: showsOnline && view !== 'offline',
    offlineHint: view !== 'online' && venue.summary.offline === null,
    onlineHint: view !== 'offline' && !hasOnlineHalf,
  };
}

/**
 * The page header's sub line (T.Z48). It used to promise «офлайн-список — только
 * чтение» to every curator, including the online-only one whose scope has no offline
 * venue to read — the one offline sentence the D6-driven hiding left on the page.
 * With a half: both clauses. Without: the online clause only.
 */
export function cabinetHeaderSub(hasOffline: boolean): string {
  return hasOffline
    ? 'Ваши площадки: офлайн-список — только чтение, онлайн-набор можно дополнять и править.'
    : 'Ваши площадки: только онлайн-набор — его можно дополнять и править.';
}

/** The online numbers of a whole scope, summed venue by venue. */
export function aggregateOnline(venues: readonly CuratorVenue[]): {
  prefillTotal: number;
  onlineAssigned: number;
  found: number;
  notFound: number;
  ambiguous: number;
} {
  let prefillTotal = 0;
  let onlineAssigned = 0;
  let found = 0;
  let notFound = 0;
  let ambiguous = 0;
  for (const venue of venues) {
    if (venue.summary.online === null) continue;
    prefillTotal += venue.summary.online.prefillTotal;
    onlineAssigned += venue.summary.online.onlineAssigned;
    found += venue.summary.online.found;
    notFound += venue.summary.online.notFound;
    ambiguous += venue.summary.online.ambiguous;
  }
  return { prefillTotal, onlineAssigned, found, notFound, ambiguous };
}

/** The freshest sync of a scope — «данные от …» for the aggregated blocks. */
export function scopeFreshness(venues: readonly CuratorVenue[]): { evi: string | null; unti: string | null } {
  let evi: string | null = null;
  let unti: string | null = null;
  for (const venue of venues) {
    if (venue.lastEviSnapshotAt !== null && (evi === null || venue.lastEviSnapshotAt > evi)) evi = venue.lastEviSnapshotAt;
    if (venue.lastUntiSyncAt !== null && (unti === null || venue.lastUntiSyncAt > unti)) unti = venue.lastUntiSyncAt;
  }
  return { evi, unti };
}

// ---------------------------------------------------------------------------
// The summary chips as filters (T.Z14/C2 + C6)
// ---------------------------------------------------------------------------

/**
 * The three buckets the online summary counts, in display order. The chip's number
 * IS the set the chip filters to — that is the whole rule:
 *
 *   - «Внесено»   → every row of the working table (what `prefillTotal` counts);
 *   - «Найдено»   → the matched rows, the ones that carry a current system status
 *                   (what `found` counts);
 *   - «Не найдено»→ the merged unmatched bucket (C6): `matchStatus` NOT_FOUND and
 *                   AMBIGUOUS together, the rows without a system status — the chip
 *                   reads `notFound + ambiguous`, because the UI shows ONE bucket.
 *
 * (The owner's round-2 note glossed «Внесено» as «matched rows»; that would make it
 * the twin of «Найдено» and contradict its own number, so the chip filters to the
 * set its number counts — the whole working table. See the commit for the note.)
 */
export type PrefillBucket = 'entered' | 'found' | 'notfound';

/** The URL key the active chips ride on, comma-joined (`?bucket=found,notfound`). */
export const PREFILL_BUCKET_PARAM = 'bucket';

export const PREFILL_BUCKETS: ReadonlyArray<{ value: PrefillBucket; label: string }> = [
  { value: 'entered', label: 'Внесено' },
  { value: 'found', label: 'Найдено' },
  { value: 'notfound', label: 'Не найдено' },
];

/** The chip set out of the URL. A token the page does not know is dropped, not 400. */
export function parsePrefillBuckets(raw: string | null | undefined): PrefillBucket[] {
  if (raw === null || raw === undefined || raw === '') return [];
  const known = PREFILL_BUCKETS.map((bucket) => bucket.value);
  return raw
    .split(',')
    .map((token) => token.trim())
    .filter((token): token is PrefillBucket => (known as string[]).includes(token));
}

/** The URL value of a chip set — `null` when none is active (the param is absent). */
export function prefillBucketsParam(buckets: readonly PrefillBucket[]): string | null {
  const unique = PREFILL_BUCKETS.map((bucket) => bucket.value).filter((value) => buckets.includes(value));
  return unique.length === 0 ? null : unique.join(',');
}

/** One click on a chip: on if it was off, off if it was on — the others stay. */
export function togglePrefillBucket(buckets: readonly PrefillBucket[], value: PrefillBucket): PrefillBucket[] {
  return buckets.includes(value) ? buckets.filter((bucket) => bucket !== value) : [...buckets, value];
}

/**
 * Does a row belong to the active chips?
 *
 * WITHIN the chips the composition is OR, on purpose: the three buckets are
 * disjoint slices of the same table, so an AND would be a promise of an empty
 * screen («Найдено» AND «Не найдено» has no member). OR gives each chip its own
 * set, and «Внесено» — the whole table — absorbs whatever it is combined with.
 *
 * ACROSS dimensions the chips AND with everything else on the page — the funnel
 * select, the search box, the venue — because those narrow the rows the server
 * returns before the chips slice the loaded page.
 */
export function prefillRowMatchesBuckets(row: CuratorPrefillRow, buckets: readonly PrefillBucket[]): boolean {
  if (buckets.length === 0) return true;
  // «Внесено» is the identity of this OR: it selects the whole table, so any set
  // containing it shows every row — exactly what its number (`prefillTotal`) counts.
  return buckets.some((bucket) => bucket === 'entered' || bucketOfRow(row) === bucket);
}

/** The one bucket a row belongs to (a row with a system status is found, else not). */
function bucketOfRow(row: CuratorPrefillRow): PrefillBucket {
  return row.currentStatus !== undefined ? 'found' : 'notfound';
}

/**
 * The number a chip shows. The merged «Не найдено» counts BOTH unmatched states
 * (C6) — the ambiguous rows are rendered as «не найдено», so the chip has to count
 * them, or the three chips would not add up to «Внесено».
 *
 * v1.9.26 (T.Z125) — the online half has TWO populations now: the working rows the
 * four summary numbers always spoke about, and the venue's ONLINE-set rows the widened
 * #26.2 streams next to them. Every ONLINE-set row carries a system status, so it
 * belongs to «Внесено» (it is a row of the table) and to «Найдено» (the system knows
 * the child) — `onlineAssigned` rides both chips, the identity «Внесено = Найдено +
 * Не найдено» survives it, and the «Внесено» chip keeps being the table's whole set
 * (`prefillTotal + onlineAssigned` = the pager's `total`).
 */
export function prefillBucketCount(
  bucket: PrefillBucket,
  online: { prefillTotal: number; onlineAssigned: number; found: number; notFound: number; ambiguous: number },
): number {
  switch (bucket) {
    case 'entered':
      return online.prefillTotal + online.onlineAssigned;
    case 'found':
      return online.found + online.onlineAssigned;
    case 'notfound':
      return online.notFound + online.ambiguous;
  }
}

// ---------------------------------------------------------------------------
// The online table's client-side sort (T.Y18)
// ---------------------------------------------------------------------------

/** The columns the «мой набор» table lets the curator sort by. */
export type PrefillSortKey = 'fullName' | 'pguId' | 'untiId';
export type SortDirection = 'asc' | 'desc';

/** The header cells that carry the sort buttons, with their RU captions. */
export const PREFILL_SORT_COLUMNS: ReadonlyArray<{ key: PrefillSortKey; label: string }> = [
  { key: 'fullName', label: 'ФИО' },
  { key: 'pguId', label: 'Номер заявления' },
  { key: 'untiId', label: 'УНТИ ID' },
];

/** The next state of a header the curator just clicked: flip, or start at `asc`. */
export function nextSortDirection(current: { key: PrefillSortKey; direction: SortDirection } | null, key: PrefillSortKey): SortDirection {
  return current !== null && current.key === key && current.direction === 'asc' ? 'desc' : 'asc';
}

/**
 * Sorts ONE loaded page (client-side on purpose — the owner asked for the columns to
 * be sortable, not for a server `sort` parameter; a page change refetches and the
 * sort reapplies to whatever arrived). Stable, so rows the key cannot tell apart
 * keep the api's «newest first» order, and NUMERIC-AWARE for the two identifiers:
 * `'9' < '10'` as numbers while the string order would put `'10'` first. A row
 * without the id sorts last in either direction — it is the one row the column says
 * nothing about.
 */
export function sortPrefillRows(
  rows: readonly CuratorPrefillRow[],
  key: PrefillSortKey,
  direction: SortDirection,
): CuratorPrefillRow[] {
  const sign = direction === 'asc' ? 1 : -1;
  return rows
    .map((row, index) => ({ row, index }))
    .sort((left, right) => {
      const result = compareBy(left.row, right.row, key, sign);
      if (result !== 0) return result;
      return left.index - right.index;
    })
    .map(({ row }) => row);
}

function compareBy(left: CuratorPrefillRow, right: CuratorPrefillRow, key: PrefillSortKey, sign: 1 | -1): number {
  if (key === 'fullName') return sign * left.fullName.localeCompare(right.fullName, 'ru');
  const leftId = left[key];
  const rightId = right[key];
  // «No id» sinks in BOTH directions — it is the one row the column says nothing
  // about, so it must not ride the direction flip back to the top.
  if (leftId === null && rightId === null) return 0;
  if (leftId === null) return 1;
  if (rightId === null) return -1;
  // Numeric-aware: both ids are digit-only, so the shorter number is the smaller
  // one and `9` lands before `10` (a plain string compare would invert them).
  const byLength = leftId.length - rightId.length;
  const ordered = byLength !== 0 ? (byLength < 0 ? -1 : 1) : leftId < rightId ? -1 : leftId > rightId ? 1 : 0;
  return sign * ordered;
}

// ---------------------------------------------------------------------------
// The venue-scoped prefill surface (T.Z27) — who may open it, and why not
// ---------------------------------------------------------------------------
/** Why the venue card's «Онлайн-набор» is (not) offered, in the order it is decided. */
export type PrefillSurfaceAccess =
  /** Render the surface — the api will answer #26.2–#26.6 for this venue. */
  | 'open'
  /** The venue does not carry `allowCuratorChildren`: there is no online set here. */
  | 'flag-off'
  /** A staff session, but this venue is outside the api's effective cabinet scope. */
  | 'out-of-scope'
  /** A КУРАТОР-role session: their surface is `/cabinet`, never the venue card's. */
  | 'role';

/**
 * The venue card's «Онлайн-набор» decision (T.Z27, owner model 2026-09-13):
 * «Куратор площадки» stays an external-curator-only setting — a МЕНЕДЖЕР never
 * becomes one and never sees the curator cabinet — but a МЕНЕДЖЕР assigned to a
 * flagged ONLINE venue manages that venue's set exactly like a curator does
 * (add children, edit/remove own unmatched rows, export). АДМИН reads the same
 * surface for the venues its links give it.
 *
 * The three facts, checked fail-closed:
 *
 *   * the ROLE — a staff role (МЕНЕДЖЕР or above); a КУРАТОР-role session is
 *     refused here on purpose (D21 makes the venue card a staff surface, and the
 *     cabinet is what the curator gets instead);
 *   * the FLAG — `allowCuratorChildren` on the venue itself: no flag, no set;
 *   * the SCOPE — the api's own effective cabinet scope (`user.cabinetVenueIds`
 *     on `/auth/me`): for a МЕНЕДЖЕР exactly the flagged venues they are linked
 *     to, for an АДМИН the union of both link sets (`ScopedVenuesService
 *     .cabinetVenueIdsOf`). The UI never recomputes that rule — it mirrors the
 *     published answer, so the section can only hide what the api would 403
 *     anyway.
 *
 * Absent or non-array scope reads as empty (fail closed), the way
 * `cabinetScopeOf` treats an api that did not publish the field.
 */
export function venuePrefillAccess(input: {
  venueId: number;
  allowCuratorChildren: boolean;
  role: unknown;
  cabinetVenueIds?: ReadonlyArray<number> | null;
}): PrefillSurfaceAccess {
  if (!hasAtLeast(input.role, 'MANAGER')) return 'role';
  if (!input.allowCuratorChildren) return 'flag-off';
  const scope = input.cabinetVenueIds;
  if (!Array.isArray(scope) || !scope.includes(input.venueId)) return 'out-of-scope';
  return 'open';
}

/** The caption each refusal shows, so the page never renders «нет данных» for «нельзя». */
export function prefillAccessCopy(access: PrefillSurfaceAccess): { title: string; hint: string } {
  switch (access) {
    case 'flag-off':
      return {
        title: 'Онлайн-набор на этой площадке не ведётся',
        hint: 'Флаг «разрешить вносить детей (онлайн-набор)» на площадке выключен — строки здесь не вносятся. Включите его в редактировании площадки.',
      };
    case 'out-of-scope':
      return {
        title: 'Онлайн-набор этой площадки вам не доступен',
        hint: 'Строки онлайн-набора ведут менеджеры, назначенные на площадку, и её куратор. Вас с этой площадкой не связывает ни одна запись.',
      };
    case 'role':
      return {
        title: '403 — не ваша роль',
        hint: 'Онлайн-набор со стороны площадки ведут менеджеры и администраторы. Куратор площадки работает со своим набором в «Кабинете».',
      };
    case 'open':
      return { title: '', hint: '' };
  }
}

// ---------------------------------------------------------------------------
// ADDITIVE (v1.9.6, T.Z62) — «Группа»: the options the two tables' selects speak,
// and the one place the group column/filter rules live
// ---------------------------------------------------------------------------

/** One option of a «Группа» select: the wire value plus the caption. */
export interface GroupOption {
  value: string;
  label: string;
}

/** The group a row carries, as the api publishes it since v1.9.6. */
export type RowGroupRef = { id: number; name: string } | null;

/**
 * The «Группа» FILTER options: «Все группы» (the empty value = the filter is off),
 * «Без группы» (the wire literal `none` — every row without a live membership) and
 * one option per group. `wholeScope` captions each option with its venue, because on
 * «Все места» the list spans several venues and two filials may name a group alike.
 */
export function groupFilterOptions(
  groups: ReadonlyArray<{ id: number; name: string; venueName?: string }>,
  options: { wholeScope?: boolean } = {},
): GroupOption[] {
  return [
    { value: '', label: 'Все группы' },
    { value: PREFILL_GROUP_NONE, label: 'Без группы' },
    ...groups.map((group) => ({
      value: String(group.id),
      label:
        options.wholeScope === true && group.venueName !== undefined && group.venueName !== ''
          ? `${group.name} · ${group.venueName}`
          : group.name,
    })),
  ];
}

/** The «Группа» select's options in a DRAWER: «Без группы» (empty) + the venue's groups. */
export function groupAssignOptions(groups: ReadonlyArray<{ id: number; name: string }>): GroupOption[] {
  return [{ value: '', label: 'Без группы' }, ...groups.map((group) => ({ value: String(group.id), label: group.name }))];
}

/** The «Группа» cell: the group's name, or the quiet «—» a row without one shows. */
export function rowGroupLabel(group: RowGroupRef): string {
  return group === null ? '—' : group.name;
}

// ── ADDITIVE (v1.9.46, T.Z166) — the офлайн-flag of «Онлайн-набор» ──────────────────

/**
 * Is this working row a child that already stands in an офлайн-таблице? The api's own
 * fact (`matchedApplicationFormat: 'OFFLINE'` on #26.2, v1.9.46) — the SPA never
 * re-derives it from a status or an id, because the curator-children matcher is the
 * only writer of the match and `format` is the application's own column. A
 * `'PREFILL'` row only: an `'ONLINE_ROSTER'` row IS an ONLINE application by its own
 * predicate, and an api that predates the key answers nothing here.
 */
export function isOfflineFormatRow(row: Pick<CuratorPrefillRow, 'matchedApplicationFormat'>): boolean {
  return row.matchedApplicationFormat === 'OFFLINE';
}

/** The per-row hint under the status badge — what the curator is asked to do. */
export const OFFLINE_ROW_HINT = 'ребёнок уже в офлайн-таблице — удалите из онлайн-набора';

/**
 * The top notice above the table, count and wording in one place — the owner's text
 * (22.09), with the noun chosen by the RU plural so the sentence stays grammatical at
 * any N: «1 ребёнок из онлайн-набора имеет офлайн-формат — такие дети уже в
 * офлайн-таблице своих площадок; удалите их из онлайн-набора». `0` answers `null` —
 * the notice does not render at all, never «0 детей».
 */
export function offlineFormatNotice(count: number | null | undefined): string | null {
  const value = typeof count === 'number' && Number.isFinite(count) ? Math.max(0, Math.round(count)) : 0;
  if (value === 0) return null;
  const noun =
    pluralRu(value, [
      'ребёнок из онлайн-набора имеет офлайн-формат',
      'ребёнка из онлайн-набора имеют офлайн-формат',
      'детей из онлайн-набора имеют офлайн-формат',
    ]);
  return `${value} ${noun} — такие дети уже в офлайн-таблице своих площадок; удалите их из онлайн-набора`;
}

/**
 * The OFFLINE roster's client-side «Группа» filter — deliberately CLIENT-side, and
 * this is the stated choice of T.Z62: #26.1 is ONE unpaginated read of one venue's
 * roster (a few hundred rows at the plan's scale), the response already carries each
 * child's group since v1.9.6, and growing the contract with a `?groupId=` the api
 * would re-filter an already-complete list buys nothing. `groupId` is a group id, the
 * `none` bucket, or `undefined` (no filter).
 */
export function offlineRowsInGroup<T extends { group: RowGroupRef }>(
  rows: readonly T[],
  groupId: number | typeof PREFILL_GROUP_NONE | undefined,
): T[] {
  if (groupId === undefined) return [...rows];
  if (groupId === PREFILL_GROUP_NONE) return rows.filter((row) => row.group === null);
  return rows.filter((row) => row.group !== null && row.group.id === groupId);
}

/**
 * The membership write a «Группа» select change asks for, folded ONCE so the two
 * drawers cannot drift: `assignTo` names the #26.12 call, `removeFrom` the #26.13
 * one, `null` = nothing to do (the same «no-op write is not sent» rule the prefill
 * PATCH keeps). A move between groups is BOTH — the api has no «move», so the drawer
 * removes first, then assigns.
 */
export function groupMembershipChange(
  current: RowGroupRef,
  next: number | null,
): { assignTo?: number; removeFrom?: number } | null {
  const currentId = current === null ? null : current.id;
  if (next === currentId) return null;
  const removeFrom = currentId === null ? undefined : currentId;
  const assignTo = next === null ? undefined : next;
  if (removeFrom === undefined && assignTo === undefined) return null;
  return { ...(removeFrom !== undefined ? { removeFrom } : {}), ...(assignTo !== undefined ? { assignTo } : {}) };
}

/**
 * Why a row's drawer keeps its «Группа» select closed, in the order the UI decides it —
 * each state an honest sentence, never a silently disabled control:
 *
 *   * `'ok'`             — the row is matched and the drawer knows its venue: the
 *                          select is live, the venue's groups are the options;
 *   * `'unmatched'`      — the row has no application yet, and #26.12 takes an
 *                          APPLICATION id: there is nothing to assign until the
 *                          matcher finds the child;
 *   * `'no-venue'`       — the drawer was opened from the «Все места» aggregate, where
 *                          a row does not name its venue: the venue's group list cannot
 *                          be fetched, and offering another venue's groups would invite
 *                          the api's 400 (a refusal the UI could have predicted is a UI
 *                          bug) — the curator is pointed at the venue view instead.
 */
export type GroupSelectGate = 'ok' | 'unmatched' | 'no-venue';

export function groupSelectGate(input: { applicationId: number | null; hasVenue: boolean }): GroupSelectGate {
  if (!input.hasVenue) return 'no-venue';
  if (input.applicationId === null) return 'unmatched';
  return 'ok';
}

/** The hint each closed gate shows under the select. */
export function groupSelectGateHint(gate: GroupSelectGate): string | null {
  switch (gate) {
    case 'ok':
      return null;
    case 'unmatched':
      return 'Группа станет доступна после сопоставления: пока у строки нет заявления, назначать некого.';
    case 'no-venue':
      return 'Группа назначается из таблицы конкретной площадки — выберите её в списке над таблицей.';
  }
}

// ---------------------------------------------------------------------------
// ADDITIVE (T.Z83) — the OFFLINE roster's filters: the online table's set, narrowed
// to what #26.1's projection can answer, applied CLIENT-side like the group filter
// of T.Z62 (same stated choice: #26.1 is ONE unpaginated read)
// ---------------------------------------------------------------------------

/**
 * The offline filter state. Deliberately NOT the online `PrefillFilters`/URL shape:
 * the two tables share one address, and the offline half's state lives in the
 * component (the T.Z62 rule) so a `?groupId=` can never collide. Every key is absent
 * = the filter is off.
 */
export interface OfflineFilters {
  /** The funnel status (`currentStatus`), as the online table's select speaks it. */
  status?: string;
  /** The search draft: ФИО / Номер заявления / УНТИ id, case-insensitive substring. */
  q?: string;
  /** The programme caption, exactly as the row stores it (`programName`). */
  programName?: string;
  /** The учебная группа — the T.Z62 filter, folded in here so one call filters all. */
  groupId?: number | typeof PREFILL_GROUP_NONE;
}

/** The fields of a #26.1 row every offline filter reads. */
export type OfflineFilterRow = {
  /** v1.9.32 (T.Z139) — `null` = the unnamed (ЕПГУ-only) row; it searches as empty. */
  fullName: string | null;
  pguId: string;
  untiId: string | null;
  programName: string | null;
  currentStatus: string;
  group: RowGroupRef;
};

/** Case-folded, so «иванов» finds «Иванов» — the api's own `?q=` reading, restated. */
function foldOfflineText(value: string): string {
  return value.toLocaleLowerCase('ru');
}

/**
 * The search box over the loaded roster (T.Z83): ONE case-insensitive substring OR
 * across ФИО, «Номер заявления» and «УНТИ ID» — the same three columns the online
 * `?q=` searches (contract v1.8.2), minus the server round-trip. An empty draft
 * filters nothing.
 */
export function offlineRowMatchesSearch(row: Pick<OfflineFilterRow, 'fullName' | 'pguId' | 'untiId'>, q: string): boolean {
  const needle = foldOfflineText(q.trim());
  if (needle === '') return true;
  // T.Z139 — an unnamed row contributes an empty ФИО term: it is found by its
  // «Номер заявления» / УНТИ id, never by a name it does not have.
  const haystack = [row.fullName ?? '', row.pguId, row.untiId ?? ''].map(foldOfflineText);
  return haystack.some((part) => part.includes(needle));
}

/**
 * «Программа» options for the offline table (T.Z83). #26.1 carries NO `courseId` —
 * the online filter's currency is a key the roster does not publish — so the options
 * are the DISTINCT programme captions the loaded rows carry, sorted RU-style. A row
 * without a programme filters to nothing (its cell reads «—»); the «Все программы»
 * first option is the caller's.
 */
export function offlineProgramOptions(rows: ReadonlyArray<Pick<OfflineFilterRow, 'programName'>>): string[] {
  const seen = new Set<string>();
  for (const row of rows) {
    const name = row.programName?.trim() ?? '';
    if (name !== '') seen.add(name);
  }
  return [...seen].sort((left, right) => left.localeCompare(right, 'ru'));
}

/**
 * The whole offline fold (T.Z83): every set filter ANDs with the others, exactly the
 * composition the online table's server-side filters keep. The group half IS
 * {@link offlineRowsInGroup} — one rule, one implementation, since T.Z62.
 */
export function offlineRowsFiltered<T extends OfflineFilterRow>(rows: readonly T[], filters: OfflineFilters): T[] {
  const byGroup = offlineRowsInGroup(rows, filters.groupId);
  return byGroup.filter((row) => {
    if (filters.status !== undefined && filters.status !== '' && row.currentStatus !== filters.status) return false;
    if (filters.programName !== undefined && filters.programName !== '' && row.programName !== filters.programName) {
      return false;
    }
    return offlineRowMatchesSearch(row, filters.q ?? '');
  });
}

/** Is ANY offline filter set? The «Сбросить фильтры» button's gate. */
export function hasOfflineFilters(filters: OfflineFilters): boolean {
  return (
    (filters.status !== undefined && filters.status !== '') ||
    (filters.q !== undefined && filters.q.trim() !== '') ||
    (filters.programName !== undefined && filters.programName !== '') ||
    filters.groupId !== undefined
  );
}

// ---------------------------------------------------------------------------
// ADDITIVE (T.Z119) — the cabinet pager: BOTH children tables show 20 rows a page.
// The online half (#26.2) pages SERVER-side (the read was already paginated — only
// the default size and the selector's options change), the offline roster (#26.1)
// pages CLIENT-side over the one unpaginated read it keeps being (the stated T.Z62
// choice — no contract change, the read stays one-shot). The fold below is the
// shared half: one page size, one slice rule, one caption idiom for both tables.
// ---------------------------------------------------------------------------

/** The per-page choices, smallest first. 20 is the default the tables open on. */
export const CABINET_PAGE_SIZE_OPTIONS: ReadonlyArray<number> = [20, 50, 100];
/** T.Z119 — «20 учеников на страницу» until the user says otherwise. */
export const CABINET_DEFAULT_PAGE_SIZE = 20;
/** `localStorage` key the choice persists under — per browser, like the theme. */
export const CABINET_PAGE_SIZE_STORAGE_KEY = 'cb.cabinet-page-size';

/**
 * The slice of `Storage` the pager needs, so a test can pass a plain object and a
 * broken storage can be simulated. The venues page keeps the same defensive shape.
 */
export type CabinetStorageLike = Pick<Storage, 'getItem' | 'setItem'>;

/** A stored choice is honoured only when it is one the selector offers — anything else (a junk write, a removed option) is the default. */
export function normalizeCabinetPageSize(value: unknown): number {
  const size = typeof value === 'number' ? value : typeof value === 'string' ? Number(value) : Number.NaN;
  return (CABINET_PAGE_SIZE_OPTIONS as readonly unknown[]).includes(size) ? size : CABINET_DEFAULT_PAGE_SIZE;
}

/**
 * The browser's storage if it can be touched at all — even READING `window.localStorage`
 * throws in a hardened browser (blocked cookies), so the access is guarded here and the
 * pager degrades to the default instead of taking the table down.
 */
export function cabinetStorage(): CabinetStorageLike | undefined {
  try {
    return window.localStorage;
  } catch {
    return undefined;
  }
}

/**
 * The stored page size, defensively: a `localStorage` that throws (private mode, a
 * denied permission) reads as «no choice», so the table keeps working and only the
 * persistence is lost — the venues roster columns' rule, restated.
 */
export function loadCabinetPageSize(storage: CabinetStorageLike | undefined): number {
  if (storage === undefined) return CABINET_DEFAULT_PAGE_SIZE;
  try {
    return normalizeCabinetPageSize(storage.getItem(CABINET_PAGE_SIZE_STORAGE_KEY));
  } catch {
    return CABINET_DEFAULT_PAGE_SIZE;
  }
}

/** Best-effort write: the on-screen table already has the value, persistence is a bonus. */
export function saveCabinetPageSize(storage: CabinetStorageLike | undefined, size: number): void {
  if (storage === undefined) return;
  try {
    storage.setItem(CABINET_PAGE_SIZE_STORAGE_KEY, String(normalizeCabinetPageSize(size)));
  } catch {
    // Persistence is best-effort by design.
  }
}

/** Page 1 is the floor; the last page that has rows is the ceiling. An empty set is page 1. */
export function clampCabinetPage(page: number, total: number, pageSize: number): number {
  const size = Math.max(1, pageSize);
  const last = Math.max(1, Math.ceil(Math.max(0, total) / size));
  return Math.min(Math.max(1, Math.floor(page) || 1), last);
}

/** The rows of ONE page, over whichever list the table shows (server page or client slice). */
export function cabinetPageSlice<T>(rows: readonly T[], page: number, pageSize: number): T[] {
  const size = Math.max(1, pageSize);
  const current = clampCabinetPage(page, rows.length, size);
  return rows.slice((current - 1) * size, current * size);
}

/** The «X–Y» window of a page, 1-based and clamped to the total — the caption's numbers. */
export function cabinetRangeOf(page: number, total: number, pageSize: number): { from: number; to: number; total: number } {
  const size = Math.max(1, pageSize);
  const safeTotal = Math.max(0, total);
  const current = clampCabinetPage(page, safeTotal, size);
  return {
    from: safeTotal === 0 ? 0 : (current - 1) * size + 1,
    to: Math.min(current * size, safeTotal),
    total: safeTotal,
  };
}

/**
 * The pager caption, in the roster note's own idiom («Показаны дети выбранного
 * фильтра: N из M.» → «Показаны дети 1–20 из 45.»): `subject` is «дети» on the
 * offline roster and «строки» on the online table, the words their notes already use.
 */
export function cabinetRangeCaption(subject: string, page: number, total: number, pageSize: number): string {
  const { from, to, total: safeTotal } = cabinetRangeOf(page, total, pageSize);
  if (safeTotal === 0) return `Показаны ${subject} 0 из 0.`;
  return `Показаны ${subject} ${from}–${to} из ${safeTotal}.`;
}

// ---------------------------------------------------------------------------
// ADDITIVE (v1.9.64, T.Z195) — the chase columns: «Договор», «Дедлайн договора»,
// «Зачислен», «Не перешёл». The curators of «Кабинет куратора» could not see who had
// signed, when the contract clock ran out or who had been enrolled, so the funnel to
// «зачислен» stalled on their children. The api computes the facts (#26.1/#26.2,
// contract v1.9.64) with the register's OWN helpers; this fold only names them —
// every view below is the register's (`contractCellText`, `contractDeadlineView`,
// `enrolledView`, the shared `eviFailedChip`), so a cabinet cell and a register cell
// of the same child can never drift apart. NO signer identity anywhere: the api does
// not send `signerName` to a curator surface and this model has no key for it.
// ---------------------------------------------------------------------------

/** The row slice the chase fold reads — both children tables' rows answer it. */
export type ChaseRow = Pick<
  CuratorChild,
  'contractState' | 'contractSignedAt' | 'contractDeadlineAt' | 'contractDaysLeft' | 'enrolled' | 'eviFailed' | 'eviFailedManually'
>;

/** The «Договор» cell: the pill's label and ink, `null` = the quiet «—» (no fact yet). */
export interface CabinetContractChip {
  label: string;
  tone: 'ok' | 'bad';
}

/** The «Не перешёл» cell: the register's chip label plus the tooltip that maps the word. */
export interface CabinetEviFailedChip {
  label: string;
  title: string;
}

/** The four chase cells of one row, everything the two tables render. */
export interface CabinetChaseView {
  /** «Договор» — «Подписан dd.mm.yyyy» / «Не подписан» / «Не пришёл», `null` = «—». */
  contract: CabinetContractChip | null;
  /** «Дедлайн договора» — the register's own view (running clock or the calm signed line). */
  deadline: ReturnType<typeof contractDeadlineView>;
  /** The `title` the deadline cell carries — the register's own sentence. */
  deadlineTitle: string;
  /** «Зачислен» — the register's own view (Да / Нет / —). */
  enrolled: ReturnType<typeof enrolledView>;
  /** «Не перешёл» — the register's «Провалил ЕВИ» chip, `null` = the quiet «—». */
  eviFailed: CabinetEviFailedChip | null;
}

/** The clause the cabinet's «Не перешёл» tooltip appends to the register's own title. */
export const EVI_FAILED_CABINET_NOTE = 'В кабинете колонка называется «Не перешёл» — это та же пометка.';

/**
 * The chase cells of ONE children row — the register's folds over the api's facts,
 * computed per row and pure (no clock here: the api spent the request instant, the
 * render does not re-spend it). An api (or a stub) that predates the keys answers the
 * same quiet rest values as an unmatched row: «—», «—», «—», no chip.
 */
export function cabinetChaseView(row: ChaseRow): CabinetChaseView {
  // The cabinet rows keep the keys OPTIONAL (an older api answers none of them); the
  // register's views take them required-with-null (#3's shape). Absent reads as null —
  // the same «no fact» the register's own `== null` checks treat it as.
  const facts = {
    contractState: row.contractState ?? null,
    contractSignedAt: row.contractSignedAt ?? null,
    contractDeadlineAt: row.contractDeadlineAt ?? null,
    contractDaysLeft: row.contractDaysLeft ?? null,
    enrolled: row.enrolled ?? null,
  };

  // «Договор» — the register's own cell text («Подписан dd.mm.yyyy» included) and its
  // own tone map; `contractCellText` answers `null` when the sync has not spoken.
  const contractLabel = contractCellText(facts);
  const contract =
    contractLabel === null || facts.contractState === null
      ? null
      : { label: contractLabel, tone: CONTRACT_STATE_TONE[facts.contractState] };

  // «Зачислен» — the register's Да / Нет / — view verbatim.
  const enrolled = enrolledView(facts);

  // «Не перешёл» — the shared fold of the two «Провалил ЕВИ» facts (auto + manual,
  // the SAME fold the register's chip speaks), and the tooltip carries the curators'
  // word for it so «не перешёл» and «Провалил ЕВИ» never read as two different facts.
  const eviFailedChipView = eviFailedChip(row);
  const eviFailed =
    eviFailedChipView === null
      ? null
      : { label: eviFailedChipView.label, title: `${EVI_FAILED_CHIP_TITLE} ${EVI_FAILED_CABINET_NOTE}` };

  return {
    contract,
    // The deadline view is `null` on an unsigned row without a running clock (the «—»)
    // and the calm «Подписан dd.mm.yyyy» on a signed one — the register's rule verbatim.
    deadline: contractDeadlineView(facts),
    deadlineTitle: CONTRACT_DEADLINE_CHIP_TITLE,
    enrolled,
    eviFailed,
  };
}
